import * as Command from '../parser/commands';
import { AstNode } from '../types';
import { PrinterContext } from './printer';
import { escposCodepagesMapping, parseTextBytes } from './text';

/**
 * General AST walker to set printerContext correctly without messing around
 * with rendering and other things (including the analyzer)
 *
 * Put all escpos command that will **only** alter the printerContext (and
 * nothing else) here
 */
export function* walkAST(ast: AstNode[], printerContext: PrinterContext): Generator<[number, AstNode, string, boolean]> {
  for (const [index, node] of ast.entries()) {
    const textData = parseTextBytes(
      node.extractedData?.text || [],
      printerContext.getTextState().encoding === 'codepage' ? printerContext.getTextState().codepage : 'unicode'
    );
    let commandHandled = true;

    switch (node.command) {
      case Command.initializePrinter:
        printerContext.resetStates();
        break;

      case Command.turnEmphasizedMode: {
        const [n] = node.parsedArgs.map((val) => val.toString(2));
        printerContext.setBold(Boolean(n[n.length - 1]));
        break;
      }

      case Command.setLineSpacing: {
        const [n] = node.parsedArgs as number[];
        printerContext.setLineSpacing(n);
        break;
      }

      case Command.selectCharPrintingMode: {
        const [n] = node.parsedArgs as number[];
        // Get each bits for each related feature
        const [isBold, isDoubleHeight, isDoubleWidth, isUnderline] = [3, 4, 5, 7].map((val) => Boolean((n >> val) & 0x1));
        printerContext.setBold(isBold);
        printerContext.setUnderline(isUnderline);
        printerContext.setWidth(isDoubleWidth ? 2 : 1);
        printerContext.setHeight(isDoubleHeight ? 2 : 1);
        break;
      }

      case Command.selectCharSize: {
        const [n] = node.parsedArgs as number[];
        // Get lower 3 bits and upper 3 bits
        const [height, width] = [0, 4].map((val) => ((n >> val) & 0x7) + 1);
        printerContext.setWidth(width);
        printerContext.setHeight(height);
        break;
      }

      case Command.selectCodeConversion: {
        const [, , , m] = node.parsedArgs as number[];
        switch (m) {
          case 2:
          case 50:
            printerContext.setEncoding('unicode');
            break;

          case 1:
          case 49:
          default:
            printerContext.setEncoding('codepage');
            break;
        }
        break;
      }

      case Command.setPositionAlignment: {
        const [n] = node.parsedArgs as number[];
        switch (n) {
          case 0:
          case 48:
            printerContext.setPosition('left');
            break;

          case 1:
          case 49:
            printerContext.setPosition('center');
            break;

          case 2:
          case 50:
            printerContext.setPosition('right');
            break;
        }
        break;
      }

      case Command.selectHRICharacterPrintPos: {
        const [n] = node.parsedArgs as number[];
        switch (n) {
          case 0:
          case 48:
            printerContext.setBarcodePrintPosition('nope');
            break;

          case 1:
          case 49:
            printerContext.setBarcodePrintPosition('top');
            break;

          case 2:
          case 50:
            printerContext.setBarcodePrintPosition('bottom');
            break;

          case 3:
          case 51:
            printerContext.setBarcodePrintPosition('both');
            break;

          default:
            break;
        }
        break;
      }

      case Command.selectHRICharacterFont: {
        const [n] = node.parsedArgs as number[];
        printerContext.setBarcodeHRICharFont(n);
        break;
      }

      case Command.setBarcodeWidth: {
        const [n] = node.parsedArgs as number[];
        printerContext.setBarcodeWidth(n);
        break;
      }

      case Command.setBarcodeHeight: {
        const [n] = node.parsedArgs as number[];
        printerContext.setBarcodeHeight(n);
        break;
      }

      case Command.printBarcode: {
        const [m] = node.parsedArgs as number[];
        switch (m) {
          case 0:
          case 65:
            printerContext.setBarcodeType('upca');
            break;

          case 1:
          case 66:
            printerContext.setBarcodeType('upce');
            break;

          case 2:
          case 67:
            printerContext.setBarcodeType('ean13');
            break;

          case 3:
          case 68:
            printerContext.setBarcodeType('ean8');
            break;

          case 4:
          case 69:
            printerContext.setBarcodeType('code39');
            break;

          case 5:
          case 70:
            printerContext.setBarcodeType('interleaved2of5');
            break;

          case 6:
          case 71:
            printerContext.setBarcodeType('rationalizedCodabar');
            break;

          case 72:
            printerContext.setBarcodeType('rationalizedCodabar');
            break;

          case 73:
            printerContext.setBarcodeType('code128');
            break;

          case 74:
            printerContext.setBarcodeType('gs1-128');
            break;

          case 75:
            printerContext.setBarcodeType('databaromni');
            break;

          case 76:
            printerContext.setBarcodeType('databartruncated');
            break;

          case 77:
            printerContext.setBarcodeType('databarlimited');
            break;

          case 78:
            printerContext.setBarcodeType('databarexpanded');
            break;

          case 79:
            printerContext.setBarcodeType('code128-auto');
            break;
        }

        printerContext.setCodeBuffer([...printerContext.getCodeBuffer(), node.extractedData?.code]);
        break;
      }

      case Command.print2dCodes: {
        const [, , cn, fn] = (node.parsedArgs as number[]).slice(0, 4);

        switch (cn) {
          case 48:
            switch (fn) {
              case 65: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                printerContext.setPdf417ColSize(n);
                break;
              }

              case 66: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                printerContext.setPdf417RowSize(n);
                break;
              }

              case 67: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                printerContext.setPdf417Width(n);
                break;
              }

              case 68: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                printerContext.setPdf417Height(n * printerContext.getPdf417State().width);
                break;
              }

              case 69: {
                const [m, n] = node.parsedArgs.slice(node.parsedArgs.length - 2) as number[];
                if (m === 48) {
                  printerContext.setPdf417ErrorCorrectionLevel(n % 48);
                }

                if (m === 49) {
                  printerContext.setPdf417ErrorCorrectionLevel(Math.ceil(n / 5));
                }
                break;
              }

              case 70: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                if (n === 0) {
                  printerContext.setPdf417Type('standard');
                } else {
                  printerContext.setPdf417Type('truncated');
                }
                break;
              }

              case 80: {
                printerContext.setCodeBuffer([...printerContext.getCodeBuffer(), node.extractedData?.code]);
                break;
              }
            }
            break;

          case 49:
            switch (fn) {
              case 65: {
                const [n1] = node.parsedArgs.slice(node.parsedArgs.length - 2) as number[];
                if (n1 === 49) {
                  printerContext.setQRCodeModel('1');
                } else if (n1 === 50) {
                  printerContext.setQRCodeModel('2');
                } else {
                  printerContext.setQRCodeModel('micro');
                }
                break;
              }

              case 67: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                printerContext.setQRCodeSize(n);
                break;
              }

              case 69: {
                const n = node.parsedArgs[node.parsedArgs.length - 1] as number;
                if (n === 48) {
                  printerContext.setQRCodeErrorCorrectionLevel('L');
                } else if (n === 49) {
                  printerContext.setQRCodeErrorCorrectionLevel('M');
                } else if (n === 50) {
                  printerContext.setQRCodeErrorCorrectionLevel('Q');
                } else {
                  printerContext.setQRCodeErrorCorrectionLevel('H');
                }
                break;
              }

              case 80: {
                printerContext.setCodeBuffer([...printerContext.getCodeBuffer(), node.extractedData?.code]);
                break;
              }
            }
            break;

          default:
            break;
        }
        break;
      }

      case Command.selectCharCodeTable: {
        const [n] = node.parsedArgs as number[];
        const codepagesMapping = escposCodepagesMapping();

        if (!Object.keys(codepagesMapping).includes(String(n))) {
          console.log(`[Warning]: Codepage ${n} is not supported yet`);
          break;
        }

        printerContext.setCodepage(codepagesMapping[n]);
        break;
      }

      default:
        commandHandled = false;
        break;
    }

    yield [index, node, textData, commandHandled];
  }
}
